/**
 * Stylesheet rule for the `counterfeit` edge type (NS-09, ALGORITHM_FRONTEND_CONTRACT section 5).
 *
 * The backend declares meaning only: `edge_type: "counterfeit"`, a `kind` of `counterfeit_token` or
 * `address_poisoning`, and the reason (`counterfeit_reason`, `poisoning_candidate`). Everything about how
 * that looks lives here (R23): a muted, dashed, arrowless line, clearly not money flow. The label is
 * read from the declared data, never from a backend string.
 */

export const COUNTERFEIT_EDGE_TYPE = 'counterfeit';

const REASON_TEXT: Record<string, string> = {
  homoglyph: 'look-alike symbol',
  unregistered_contract: 'unregistered contract',
};

type Getter = { data: (k: string) => unknown };

function declared(ele: Getter, key: string): unknown {
  const direct = ele.data(key);
  if (direct !== undefined && direct !== null && direct !== '') return direct;
  const attr = ele.data('attribution') as Record<string, unknown> | undefined;
  return attr && typeof attr === 'object' ? attr[key] : undefined;
}

/** Label / tooltip text for a counterfeit edge, from the declared kind and reason. */
export function counterfeitEdgeLabel(ele: Getter): string {
  const kind = String(declared(ele, 'kind') ?? '');
  const reason = String(declared(ele, 'counterfeit_reason') ?? '');
  const poisoning = kind === 'address_poisoning' || declared(ele, 'poisoning_candidate') === true;
  if (poisoning) return 'look-alike address (poisoning attempt)';
  const why = REASON_TEXT[reason];
  return why ? `counterfeit token (${why})` : 'counterfeit token';
}

/** Cytoscape rules; spread into the canvas stylesheet after the money-flow base rule. */
export function counterfeitEdgeStyles(isDark: boolean): Array<{ selector: string; style: Record<string, unknown> }> {
  const style = {
    'width': 1,
    'line-color': isDark ? '#52525b' : '#a8b3c4',
    'line-style': 'dashed',
    'line-dash-pattern': [3, 5],
    'target-arrow-shape': 'none',
    'source-arrow-shape': 'none',
    'curve-style': 'bezier',
    'opacity': 0.55,
    'label': (ele: Getter) => counterfeitEdgeLabel(ele),
    'text-opacity': 0.8,
    'font-size': 7,
    'font-style': 'italic',
  };
  return [
    { selector: `edge[edge_type = "${COUNTERFEIT_EDGE_TYPE}"]`, style },
    { selector: `edge[edgeType = "${COUNTERFEIT_EDGE_TYPE}"]`, style },
  ];
}
